import { Component, OnInit, ViewEncapsulation, Type } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormGroup, FormArray } from '@angular/forms';
import { AutoCompleteAddressModel } from 'src/models/autocomplete-address.model';
import { AddressComponentsModel, AddressForm } from 'src/providers/address-form/address-form';
import { MultipleCountryService } from 'src/providers/addresses/multiple-country.service';
import { GoogleGeocode } from 'src/providers/google-geocode/google-geocode';
import { RadioButtonModel } from 'src/tidy-ui-components/components/form/radio-button/radio-button.model';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Client } from 'src/providers/client/client';
import { Util } from 'src/shared/util/util';
import { Addresses } from 'src/providers/customer/addresses';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { OnboardingFlow } from 'src/shared/enums/onboarding-flow';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { DirectIntegrationOnboardingComponent } from '../direct-integration/direct-integration-onboarding.component';
import { ImportCSVOnboardingComponent } from '../import-csv/import-csv-onboarding.component';

// Component imports for flow-to-component mapping (redirect after property creation)
import { SelectAccessNotesOptionOnboarding } from '../select-access-notes-option-onboarding/select-access-notes-option-onboarding';
import { AddProsOnboardingComponent } from '../add-pros/add-pros.component';
import { EnableAutomaticBookingOnboarding } from '../enable-automatic-booking-onboarding/enable-automatic-booking-onboarding';
import { SelectRestockingOptionOnboarding } from '../select-restocking-option-onboarding/select-restocking-option-onboarding';
import { SelectMaintenanceOptionOnboarding } from '../select-maintenance-option-onboarding/select-maintenance-option-onboarding';
import { ToDosModalOnboardingComponent } from '../to-dos/to-dos.component';
import { EnableGuestSmartNumberOnboarding } from '../enable-guest-smart-number-onboarding/enable-guest-smart-number-onboarding';
import { EnableConciergeMessagingOnboarding } from '../enable-concierge-messaging-onboarding/enable-concierge-messaging-onboarding';
import { NotifyProsOfChangeOnboarding } from '../notify-pros-of-change-onboarding/notify-pros-of-change-onboarding';
import { SelectFindNewProsOptionOnboarding } from '../select-find-new-pros-option-onboarding/select-find-new-pros-option-onboarding';
import { SelectIntegrationOnboarding } from '../select-integration-onboarding/select-integration-onboarding';
import { BillsOnboardingComponent } from '../bills/bills.component';
import { AutomateWorkflowsOnboarding } from '../automate-workflows-onboarding/automate-workflows-onboarding';
import { CreateConciergeRulesOnboarding } from '../create-concierge-rules-onboarding/create-concierge-rules-onboarding';
import { RecurringTaskComponent } from 'src/pages/tasks/recurring-tasks/recurring-task/recurring-task';

interface PropertyAddress {
  address: string;
  address2?: string;
  countryCode: string;
  city?: string;
  state?: string;
  zipcode?: string;
  latitude?: number;
  longitude?: number;
  addressNickname?: string;
}

@Component({
    selector: 'tidy-property-onboarding',
    templateUrl: './property-onboarding.component.html',
    styleUrls: ['./property-onboarding.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class PropertyOnboardingComponent implements OnInit {

  submitted = false;
  errorMessage: string;
  showNoPropertiesContent = false;
  forms: FormArray;
  countryList: RadioButtonModel[];
  autocompleteAddress: AutoCompleteAddressModel[] = [];
  radioButtonAddress: RadioButtonModel[] = [];
  didChooseAddress = false;
  typedOnAddress = false;
  zipCodeMask = {
    mask: '00000' // US mask
  };
  countryCode: string;

  // Flow-to-component mapping for redirect after property creation
  private readonly flowToComponentMap: { [key: string]: Type<any> } = {
    'selectIntegration': SelectIntegrationOnboarding,
    'enableAutomaticBooking': EnableAutomaticBookingOnboarding,
    'selectAccessNotes': SelectAccessNotesOptionOnboarding,
    'addPros': AddProsOnboardingComponent,
    'selectFindNewPros': SelectFindNewProsOptionOnboarding,
    'inventoryAutomation': SelectRestockingOptionOnboarding,
    'selectMaintenance': SelectMaintenanceOptionOnboarding,
    'toDos': ToDosModalOnboardingComponent,
    'enableGuestSmartNumber': EnableGuestSmartNumberOnboarding,
    'messageAutomation': EnableConciergeMessagingOnboarding,
    'notifyProsOfChange': NotifyProsOfChangeOnboarding,
    'bills': BillsOnboardingComponent,
    'automateWorkflows': AutomateWorkflowsOnboarding,
    'createConciergeRules': CreateConciergeRulesOnboarding,
    'addRecurringMaintenance': RecurringTaskComponent,
  };

  boundAddProperty = this.addProperty.bind(this);

constructor(
    private onboardingProvider: OnboardingProvider,
    private formBuilder: UntypedFormBuilder,
    private addressForm: AddressForm,
    private fb: UntypedFormBuilder,
    private multipleCountryService: MultipleCountryService,
    private googleGeocode: GoogleGeocode,
    private addresses: Addresses,
    private client: Client,
    private currentAddress: CurrentAddress,
    private util: Util,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService
  ) {
    this.forms = this.fb.array([]);
    this.forms.push(this.fb.group({
      countryCode: ['US', Validators.required],
      address: ['', Validators.compose([Validators.maxLength(100), Validators.required])],
      address2: ['', Validators.compose([Validators.maxLength(100)])],
      zipcode: ['', this.multipleCountryService.getCountrySettings('US').validator],
      latitude: '',
      longitude: '',
      city: '',
      state: '',
      addressNickname: [''],
      phone: [''],
    }));
    this.countryList = this.multipleCountryService.countryRadioButtonList;
  }

  ngOnInit() {
    this.onboardingProvider.priorPages.push('property');
  }

  async goToAddDirectIntegration() {
    await this.storage.save('cameFromIntegrateOnboarding', false);
    await this.storage.save('cameFromPropertyOnboarding', true);
    this.rightSidePanelService.setDialogPageTitle('Connect Integration');
    this.rightSidePanelService.navigateTo('onboarding/directIntegration', {}, DirectIntegrationOnboardingComponent);
  }

  async goToImportCSV() {
    this.rightSidePanelService.setDialogPageTitle('Import CSV');
    this.rightSidePanelService.navigateTo('onboarding/importCSV', {}, ImportCSVOnboardingComponent);
  }

  async addProperty() {
    this.submitted = true;
    if (!this.forms.valid) {
      this.util.showWarning('Please fill out all fields');
      return;
    }
    try {
      const settings = await this.packageSettings();
      const [createdAddresses, _settingsResponse] = await Promise.all([
        this.submitProperties(),
        this.client.saveClientSettings(settings)
      ]);
      if (!createdAddresses) {
        return;
      }
      localStorage.setItem('addressId', createdAddresses[0].id);
      localStorage.removeItem('taskListId');
      const _moreDetailAddresses = await this.client.getMoreDetailAddresses(true);

      // Check if we need to redirect to a flow after property creation
      const dialogParams = await this.storage.retrieve('dialog-params');
      if (dialogParams?.redirectAfterProperty) {
        const targetFlow = dialogParams.redirectAfterProperty;
        const targetComponent = this.flowToComponentMap[targetFlow];
        const targetTitle = dialogParams.redirectTitle || targetFlow;

        if (targetComponent) {
          await this.storage.save('dialog-params', { flow: targetFlow });
          this.rightSidePanelService.setDialogPageTitle(targetTitle);
          this.rightSidePanelService.navigateTo(`onboarding/${targetFlow}`, {}, targetComponent);
          this.util.showSuccess('Properties added successfully');
          return;
        }
      }

      // Legacy flow handling
      if (this.onboardingProvider.flow === 'schedule') {
        this.onboardingProvider.setShowOnboardingOnPage(false);
        this.onboardingProvider.setShowOnboardingInHeader(true);
        this.rightSidePanelService.closeRightSidePanel();
      } else if (this.onboardingProvider.flow !== 'property') {
        this.onboardingProvider.showBackButton = false;
        this.onboardingProvider.priorPages = [];
        this.onboardingProvider.page = this.onboardingProvider.flow;
        this.onboardingProvider.shouldShowAddProperty = false;
        this.onboardingProvider.setOnboardingFlowText();
        this.onboardingProvider.setShowOnboardingInHeader(true);
      } else {
        this.onboardingProvider.setShowOnboardingOnPage(false);
        this.onboardingProvider.setShowOnboardingInHeader(true);
        this.rightSidePanelService.closeRightSidePanel();
        this.navCtrl.navigateForward(`properties?propertyId=${createdAddresses[0].id}`);
      }
      this.util.showSuccess('Properties added successfully');
    } catch (err) {
      this.util.showError('Error adding properties', err.error?.message || err.message);
    }
  }

  async packageSettings() {
    const settings = await this.client.getClientSettings();
    const phoneValue = this.forms.value[0].phone;
    return {
      profile: {
        last_name: settings.profile.last_name,
        first_name: settings.profile.first_name,
        email: settings.profile.email,
        phone: phoneValue && this.countryCode ?
          parsePhoneNumber(this.countryCode + phoneValue) :
          phoneValue,
        account_type: OnboardingFlow.RENTAL
      }
    }
  }

  async chooseItem(item: { placeId: string, value: string | number, display: string}, form: UntypedFormGroup) {
    const zipcode = await this.addressForm.chooseItem(item);
    if (!zipcode && this.multipleCountryService.checkIfContryCodeIsUs(form.get('countryCode').value)) {
      this.errorMessage = `It looks like this address isn't detailed enough, please update it. If you still have issues please contact us in the Concierge section.`;
    }
    const address: AddressComponentsModel = await this.addressForm.getAdditonalAddressInfo(item.placeId);
    form.patchValue({ zipcode, ...address });
    const latLngData = await this.googleGeocode.getLatLong(item.placeId);
    form.patchValue(latLngData);
    this.autocompleteAddress = [];
    this.didChooseAddress = true;
    this.typedOnAddress = false;
  }

  updateSearch = async (form: UntypedFormGroup) => {
    this.errorMessage = '';
    try {
      this.autocompleteAddress = await this.addressForm.updateSearch(form.value.address, form.get('countryCode').value);
    } catch (_err) {
      this.errorMessage = 'An error occurred when getting address information. Please type another address.';
    }
    this.radioButtonAddress = this.autocompleteAddress.map(address => ({
        value: address.value,
        viewValue: address.display,
        placeId: address.placeId
      }));
    this.typedOnAddress = true;
    this.didChooseAddress = false;
  }

  changeCountrySelected(countryCodeSelected, form: UntypedFormGroup) {
    const validatorObject = this.multipleCountryService.getCountrySettings(countryCodeSelected);
    form.get('zipcode').setValidators(validatorObject?.validator);
    this.zipCodeMask = validatorObject?.mask;
    setTimeout(() => {
      form.patchValue({
        address: null,
        address2: null,
        zipcode: null
      });
    });
  }

  addAnotherProperty() {
    this.forms.push(this.fb.group({
      countryCode: ['US', Validators.required],
      address: ['', Validators.compose([Validators.maxLength(100), Validators.required])],
      address2: ['', Validators.compose([Validators.maxLength(100)])],
      zipcode: ['', this.multipleCountryService.getCountrySettings('US').validator],
      latitude: '',
      longitude: '',
      city: '',
      state: '',
      addressNickname: [''],
      phone: [''],
    }));
  }

  removeProperty(form: UntypedFormGroup) {
    this.forms.removeAt(this.forms.controls.indexOf(form));
  }

  onCountryCodeChange(countryCode: string) {
    this.countryCode = countryCode.replace('+', '');
  }

  async validateAndCreatePayload(properties: PropertyAddress[]) {
    const validatedProperties = [];
    const errors = [];
    for (let i = 0; i < properties.length; i++) {
      const property = properties[i];
      try {
        if (!property?.address) {
          throw new Error(`Property ${i + 1}: We could not validate your address with Google. Please enter an address that can be found with Google Maps.`);
        }
        const addressArray = property.address.split(',');
        if (!addressArray[2] && this.multipleCountryService.checkIfContryCodeIsUs(property.countryCode)) {
          throw new Error(`Property ${i + 1}: Address isn't detailed enough, please update it. If you still have issues please contact us in the Concierge section.`);
        }
        const addressStateFallback: string = addressArray[2]?.trim();
        const coords = await this.googleGeocode.getLatLongWithUnit(
          { address1: addressArray[0].trim(), address2: property.address2, city: property.city, state: property.state || addressStateFallback, zip: property.zipcode },
          { latitude: property.latitude, longitude: property.longitude }
        );
        const propertyPayload = {
          country_code: property.countryCode,
          add_state: property.state || addressStateFallback,
          city: property.city,
          address1: addressArray[0].trim(),
          address2: property.address2,
          zip: property.zipcode,
          latitude: coords.latitude,
          longitude: coords.longitude,
        };
        if (property.addressNickname) {
          propertyPayload['address_name'] = property.addressNickname;
        }
        validatedProperties.push(propertyPayload);
      } catch (error) {
        errors.push(error.message);
      }
    }
    if (errors.length > 0) {
      this.errorMessage = errors.join('\n');
      this.util.showError(this.errorMessage);
      return null;
    }
    return validatedProperties;
  }

  async submitProperties() {
    try {
      const validatedProperties = await this.validateAndCreatePayload(this.forms.value);
      if (!validatedProperties) {
        return;
      }
      const createdAddresses = await this.addresses.createManyAddresses({ addresses: validatedProperties });
      if (createdAddresses.length > 0) {
        localStorage.setItem('region_id', createdAddresses[0].region_id);
      }
      await this.client.getAddresses(true);
      await this.client.getMoreDetailAddresses(true);
      return createdAddresses;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage);
      return null;
    }
  }

}
